/**
 * The notification centre's bell (resources/views/layouts/partials/_notification-bell.blade.php).
 *
 * The list loads each time the bell opens. The unread badge is rendered with the page, then kept live over
 * Reverb (App\Events\InboxChanged on the user's App.User.{id} channel) while the tab is visible. A page that
 * doesn't use websockets for anything else only connects once it's idle, and drops the connection when the tab
 * has been hidden for a while; coming back reconnects and checks the count, so nothing is missed.
 */

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content ?? '';

/** How long the tab has to be hidden before the badge is checked again. */
const RECHECK_AFTER_MS = 60 * 1000;

/** How long the tab has to be hidden before a connection the bell opened itself is closed. */
const DISCONNECT_AFTER_MS = 30 * 1000;

function setBadge(bell, unread) {
    const badge = bell.querySelector('[data-notification-badge]');
    const button = bell.querySelector('[data-bs-toggle="dropdown"]');
    if (!badge) {
        return;
    }

    badge.textContent = unread > 9 ? '9+' : String(unread);
    badge.classList.toggle('d-none', unread === 0);
    button?.setAttribute('aria-label', unread ? `Notifications, ${unread} unread` : 'Notifications');
}

/**
 * The list's "Also send these as browser notifications" switch. Turning it on turns notifications on in this
 * browser too when they aren't yet (window.tfcPush, resources/js/push.js); a browser the user turned on
 * elsewhere is enough when this one can't.
 */
async function togglePush(bell, input) {
    const hint = bell.querySelector('[data-notification-push-hint]');
    const enabled = input.checked;
    input.disabled = true;
    hint.textContent = '';

    try {
        if (enabled && window.tfcPush.supported) {
            const { subscription } = await window.tfcPush.current();
            if (!subscription && !(await window.tfcPush.turnOn()).subscription) {
                hint.textContent = "Notifications are blocked in this browser. They'll still go to any other browser you turned them on in.";
            }
        }

        const response = await fetch(bell.dataset.pushUrl, {
            method: 'PUT',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrfToken(),
                'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ enabled, kind: input.dataset.notificationPush || 'inbox' }),
        });
        if (!response.ok) {
            throw new Error(`Saving the push setting failed (${response.status})`);
        }

        const data = await response.json();
        input.checked = data.enabled;
        if (data.enabled && data.browsers === 0) {
            hint.textContent = window.tfcPush.supported
                ? 'No browser has notifications on yet, so nothing will arrive until one does.'
                : "This browser can't show notifications. On an iPhone or iPad, add the site to your Home Screen and open it from there.";
        }
    } catch (error) {
        console.error(error);
        input.checked = !enabled;
        hint.textContent = error.message && !error.message.startsWith('Saving') ? error.message : "Couldn't save that. Try again.";
    } finally {
        input.disabled = false;
    }
}

function initBell(bell) {
    const menu = bell.querySelector('[data-notification-menu]');

    const load = () => fetch(bell.dataset.listUrl, { headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' } })
        .then((response) => {
            if (!response.ok) {
                throw new Error(`Loading notifications failed (${response.status})`);
            }

            return response.text();
        })
        .then((html) => {
            menu.innerHTML = html;
            const list = menu.querySelector('[data-notification-unread]');
            if (list) {
                document.querySelectorAll('[data-notification-bell]').forEach((each) => setBadge(each, Number(list.dataset.notificationUnread)));
            }
        })
        .catch((error) => {
            console.error(error);
            menu.innerHTML = '<div class="notification-list__empty">Couldn\'t load notifications. Try again.</div>';
        });

    bell.addEventListener('show.bs.dropdown', load);

    menu.addEventListener('change', (event) => {
        if (event.target.matches('[data-notification-push]')) {
            togglePush(bell, event.target);
        }
    });

    menu.addEventListener('click', (event) => {
        const button = event.target.closest('[data-notification-read-all]');
        if (!button) {
            return;
        }

        button.disabled = true;
        fetch(bell.dataset.readAllUrl, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-CSRF-TOKEN': csrfToken(), 'X-Requested-With': 'XMLHttpRequest' },
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Marking notifications read failed (${response.status})`);
                }

                return load();
            })
            .catch((error) => {
                console.error(error);
                button.disabled = false;
            });
    });
}

/**
 * Check the unread count once when the tab comes back after a while, for every bell on the page (the
 * sidebar's and the phone tab bar's).
 */
function recheck(bells) {
    fetch(bells[0].dataset.countUrl, { headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } })
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => {
            if (data) {
                bells.forEach((bell) => setBadge(bell, Number(data.unread)));
            }
        })
        .catch((error) => console.error(error));
}

/**
 * Listen for InboxChanged. Echo is loaded here only when the page hasn't loaded it already; a connection the page
 * opened for its own live updates is shared and never closed by the bell.
 *
 * @returns {Promise<{connect: Function, disconnect: Function}|null>} null when the site has no Reverb settings
 */
async function listenLive(bells) {
    const ownConnection = !window.Echo;
    if (ownConnection) {
        await import('./echo.js');
    }
    if (!window.Echo) {
        return null;
    }

    window.Echo.private(bells[0].dataset.userChannel)
        .listen('.inbox.changed', (event) => bells.forEach((bell) => setBadge(bell, Number(event.unread))));

    const pusher = window.Echo.connector?.pusher;

    return {
        connect: () => ownConnection && pusher?.connect(),
        disconnect: () => ownConnection && pusher?.disconnect(),
    };
}

function whenIdle(callback) {
    if ('requestIdleCallback' in window) {
        window.requestIdleCallback(callback, { timeout: 5000 });
    } else {
        window.setTimeout(callback, 2000);
    }
}

function watchUnread(bells) {
    let hiddenAt = null;
    let disconnectTimer = null;
    let disconnected = false;
    let live = null;
    let starting = null;

    const start = () => {
        starting ??= listenLive(bells)
            .then((connection) => {
                live = connection;
            })
            .catch((error) => console.error('Live notification badge failed to start', error));
    };

    if (bells[0].dataset.userChannel && window.siteConfig?.reverb?.key) {
        if (document.visibilityState === 'visible') {
            whenIdle(start);
        }
    }

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            hiddenAt = Date.now();
            disconnectTimer = window.setTimeout(() => {
                if (live) {
                    live.disconnect();
                    disconnected = true;
                }
            }, DISCONNECT_AFTER_MS);
            return;
        }

        window.clearTimeout(disconnectTimer);
        // A tab opened in the background starts listening the first time it's shown.
        if (!starting && bells[0].dataset.userChannel && window.siteConfig?.reverb?.key) {
            whenIdle(start);
        }

        if (disconnected) {
            live.connect();
            disconnected = false;
            recheck(bells);
        } else if (hiddenAt !== null && Date.now() - hiddenAt >= RECHECK_AFTER_MS) {
            recheck(bells);
        }
        hiddenAt = null;
    });
}

document.addEventListener('DOMContentLoaded', () => {
    const bells = [...document.querySelectorAll('[data-notification-bell]')];
    bells.forEach(initBell);
    if (bells.length) {
        watchUnread(bells);
    }
});
